import { Prisma } from "@prisma/client";

export const Decimal = Prisma.Decimal;
export type Decimal = Prisma.Decimal;

export const DECIMAL_ZERO = new Decimal(0);
export const DECIMAL_ONE = new Decimal(1);

export const DECIMAL_PRECISION = 6;

/**
 * Epsilon value for floating-point comparisons, derived from DECIMAL_PRECISION.
 * Use this for detecting tiny values that are likely floating-point noise.
 *
 * Value: 10^-DECIMAL_PRECISION = 0.000001 (when DECIMAL_PRECISION = 6)
 *
 * @example
 * // Clamp tiny negatives to zero (floating-point noise)
 * if (value.lt(0) && value.abs().lte(DECIMAL_EPSILON)) {
 *   value = DECIMAL_ZERO;
 * }
 */
export const DECIMAL_EPSILON = new Decimal(10).pow(-DECIMAL_PRECISION);

/**
 * Tolerance for comparing JS `number` values that have already crossed the
 * Decimal → number boundary (e.g. `LotConsumption.packageCost`, stored as
 * Float). Tighter than `DECIMAL_EPSILON` because float precision survives
 * ~15 significant digits, so 1e-9 rejects real divergence while absorbing
 * round-trip noise.
 */
export const FLOAT_EPSILON = 1e-9;

// =============================================================================
// SAFE DECIMAL CONVERSION
// =============================================================================

/**
 * Safely converts a value to Decimal, handling all edge cases.
 *
 * USE THIS when the input might be:
 * - A Decimal instance (from Prisma)
 * - A number (from calculations or NumberInput)
 * - A string (from text inputs or JSON)
 * - null, undefined, or empty string (from cleared inputs)
 * - A serialized Decimal (plain object after JSON boundary)
 *
 * DO NOT USE `new Decimal(value)` directly when:
 * - The value comes from form input (NumberInput returns '' when cleared)
 * - The value crosses a JSON serialization boundary
 * - The value could be null or undefined
 *
 * @example
 * // Form values - NumberInput can return number | '' | undefined
 * const qty = toDecimal(form.values.quantity);
 *
 * @example
 * // Optional Prisma fields
 * const weight = toDecimal(product.weightLbs);
 *
 * @example
 * // Replacing local helpers like:
 * // const D = (v) => new Decimal(v ?? 0);  // UNSAFE - throws on ''
 * // Use instead:
 * const weight = toDecimal(value);
 *
 * @param value - Any value that should become a Decimal
 * @param defaultValue - Value to use for null/undefined/invalid (default: 0)
 * @returns A valid Decimal instance
 */
export const toDecimal = (value: unknown, defaultValue = 0): Decimal => {
  // Already a Decimal instance - pass through (most common case from Prisma)
  if (value instanceof Decimal) {
    return value;
  }

  // Valid number - wrap in Decimal
  if (typeof value === "number" && !Number.isNaN(value) && Number.isFinite(value)) {
    return new Decimal(value);
  }

  // Non-empty string that parses as a number - wrap in Decimal
  // This handles both "123.45" and serialized Decimals that became strings
  if (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))) {
    return new Decimal(value);
  }

  // Handle serialized Decimal objects (after JSON.parse)
  // Prisma Decimals serialize to objects like { s: 1, e: 2, d: [1, 2, 3] }
  if (value !== null && typeof value === "object" && "d" in value && "e" in value && "s" in value) {
    try {
      // Attempt to reconstruct - Decimal constructor can accept its own serialized form
      return new Decimal(value as Prisma.Decimal.Value);
    } catch {
      // Fall through to default if reconstruction fails
    }
  }

  // For null, undefined, '', NaN, Infinity, objects, etc. - use default
  return new Decimal(defaultValue);
};

// =============================================================================
// DECIMAL COALESCE UTILITIES
// =============================================================================

type DecimalCoalesceInput = Decimal | null | undefined;
export function decimalCoalesce(values: DecimalCoalesceInput[]) {
  for (const value of values) {
    if (value !== null && value !== undefined && value.gt(DECIMAL_ZERO)) {
      return value;
    }
  }
  return null;
}

export function decimalCoalesceEx(values: [DecimalCoalesceInput, string][]) {
  for (const [value, label] of values) {
    if (value !== null && value !== undefined && value.gt(DECIMAL_ZERO)) {
      return label;
    }
  }
  return null;
}

/**
 * Round `value` to `precision` decimal places **only if**
 * the value has at least that many fractional digits already.
 * Otherwise, return the value unchanged.
 *
 * Examples:
 *  roundIfHasPrecision(2.153, 3) -> 2.153
 *  roundIfHasPrecision(2.153, 2) -> 2.15
 *  roundIfHasPrecision(2.153, 1) -> 2.2
 */
export const roundToPrecision = (value: Decimal, precision: number): Decimal => {
  const places = value.decimalPlaces();
  if (places < precision) {
    return value;
  }
  return value.toDecimalPlaces(precision);
};

// =============================================================================
// QUANTITY NORMALIZATION
// =============================================================================

/**
 * Normalize a quantity to DECIMAL_PRECISION (6) decimal places.
 *
 * Only rounds if the value has MORE than DECIMAL_PRECISION decimal places,
 * preserving clean values (integers, values with fewer decimals) unchanged.
 * This avoids unnecessary object allocation and preserves toString() formatting.
 *
 * USE THIS when quantities are used in:
 * - Prisma WHERE clauses for optimistic locking (e.g., `qtyAllocated: { gte: quantity }`)
 * - Comparisons against database values
 * - Storage of calculated quantities (e.g., `calculatedQuantity`)
 *
 * This prevents floating-point precision noise (e.g., `36155.0000000000000030`)
 * from causing comparison failures against clean database values.
 *
 * @example
 * // Before Prisma WHERE clause
 * const normalizedQty = normalizeQuantity(args.quantity);
 * where: { qtyAllocated: { gte: normalizedQty } }
 *
 * @example
 * // In calcCalculatedQuantity
 * return normalizeQuantity(new Decimal(quantity).mul(multiplier));
 *
 * @param value - The quantity to normalize
 * @returns The original value if already clean, or rounded to DECIMAL_PRECISION (6) decimal places
 */
export const normalizeQuantity = (value: Decimal): Decimal => {
  const places = value.decimalPlaces();
  if (places <= DECIMAL_PRECISION) {
    return value;
  }
  return value.toDecimalPlaces(DECIMAL_PRECISION, Decimal.ROUND_HALF_CEIL);
};

/** Lot reads emit derived quantities at the stored precision as plain decimals. */
export const quantityText = (value: Decimal): string => normalizeQuantity(value).toFixed();

// =============================================================================
// NEAR-ZERO / NEAR-EQUAL COMPARISONS
// =============================================================================

/**
 * Clamp a `Decimal` whose absolute value is within `DECIMAL_EPSILON` to
 * exactly zero. Use after subtractions on normalized quantities to drop
 * floating-point noise before downstream `.eq(0)` / division checks.
 */
export const clampNearZero = (value: Decimal): Decimal => {
  if (value.abs().lte(DECIMAL_EPSILON)) {
    return DECIMAL_ZERO;
  }
  return value;
};

export const normalizedQuantityDifference = (selectedQuantity: Decimal, requestedQuantity: Decimal): Decimal =>
  clampNearZero(normalizeQuantity(selectedQuantity).sub(normalizeQuantity(requestedQuantity)));

/**
 * Near-equality check for JS `number` values (post-Decimal→number boundary).
 * Defaults to `FLOAT_EPSILON` (1e-9). Pass an explicit `epsilon` only when
 * the call site has a domain-specific tolerance.
 */
export const isApproxEqual = (a: number, b: number, epsilon: number = FLOAT_EPSILON): boolean => {
  return Math.abs(a - b) < epsilon;
};

/**
 * Tolerance check between two `Decimal` values. Returns true when
 * `|a − b| ≤ tolerance`. Use for domain-specific drift tolerances
 * (e.g. operator-keyed inputs that may carry hundredth-of-a-unit
 * arithmetic noise). For pure floating-point noise on normalized
 * quantities, prefer `clampNearZero` against `DECIMAL_EPSILON`.
 */
export const withinTolerance = (a: Decimal, b: Decimal, tolerance: Decimal): boolean => {
  return a.sub(b).abs().lte(tolerance);
};
